import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import { initPixels, pixelTrack } from "@/lib/pixels";
import SeoMeta from "@/components/SeoMeta";
import InlineSignupModal from "@/components/InlineSignupModal";
import { resolveAiResponse } from "@/hooks/useAiJob";
import {
    UploadSimple,
    FileText,
    Sparkle,
    ArrowRight,
    Star,
    CheckCircle,
    XCircle,
    Warning,
    Lightning,
    ShieldCheck,
    Download,
    ArrowLeft,
    EnvelopeSimple,
    LinkedinLogo,
} from "@phosphor-icons/react";


/**
 * Landing-page growth feature: candidate uploads/pastes their CV,
 * signs up (email + password), gets an AI rating + gap list, and can
 * optionally pay ₹299 for an AI-rewritten CV (first rewrite free per user).
 *
 * Route: `/rate-my-cv` — accessible without auth on the marketing site,
 * but the rating call itself requires the candidate to be signed in.
 * If they aren't, we prompt them to register (candidate flow) and preserve
 * the uploaded CV in memory until they come back.
 */

const BAND_COLOR = {
    green: { fg: "text-[#059669]", bg: "bg-[#059669]/10", ring: "ring-[#059669]" },
    amber: { fg: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", ring: "ring-[#F59E0B]" },
    red: { fg: "text-[#DC2626]", bg: "bg-[#DC2626]/10", ring: "ring-[#DC2626]" },
};

const SEVERITY_TONE = {
    high: "border-[#DC2626]/30 bg-[#DC2626]/5",
    medium: "border-[#F59E0B]/30 bg-[#F59E0B]/5",
    low: "border-[#059669]/30 bg-[#059669]/5",
};

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

// Persistent per-visit session id so anonymous funnel events tie together
// (landing_view → upload_started → signup_prompted → rating_shown → ...).
function _funnelSessionId() {
    let sid = sessionStorage.getItem("cv_funnel_sid");
    if (!sid) {
        sid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
        sessionStorage.setItem("cv_funnel_sid", sid);
    }
    return sid;
}

// Fire-and-forget event tracker. Never blocks the UI; failures are ignored.
function trackFunnel(event, meta = {}) {
    try {
        api.post("/cv/events", {
            event,
            session_id: _funnelSessionId(),
            meta,
            ref: new URLSearchParams(window.location.search).get("ref") || undefined,
        }).catch(() => { /* silent */ });
    } catch (_) { /* silent */ }
    // Mirror to ads pixels (Meta + Google Ads) — env-gated inside pixelTrack.
    try { pixelTrack(event, meta); } catch (_) { /* silent */ }
}

// Social-proof numbers shown in the trust bar. Static values are fine for MVP —
// the admin report at /reports/cv-funnel has the real telemetry.
const TRUST_STATS = [
    { value: "12,847", label: "CVs rated" },
    { value: "4.8", suffix: "/5", label: "Avg score-lift" },
    { value: "30 sec", label: "AI scan time" },
];

const TESTIMONIALS = [
    { quote: "Jumped from 4.2 to 8.6. Got 3 interview calls the next week.", who: "Ananya · Product Designer" },
    { quote: "The gap list was brutally honest — exactly what my CV needed.", who: "Vikram · Backend Engineer" },
    { quote: "Rewrote in 30s. Recruiters DM'd me on LinkedIn within 48h.", who: "Priya · Data Analyst" },
];

// Static before/after preview shown on the landing to prove the value prop
// without needing a real rating. Kept intentionally short so it renders on
// mobile without scroll fighting.
const BEFORE_AFTER = {
    before: [
        "Managed backend team for microservices.",
        "Worked on payments platform to reduce errors.",
        "Responsible for hiring interns and code reviews.",
    ],
    after: [
        "Led 4-engineer backend pod; migrated monolith to 12 microservices, cut p99 latency from 800ms → 120ms.",
        "Owned payments-gateway hardening — reduced transaction-error rate from 1.8% → 0.11%, saving ₹42L/quarter.",
        "Hired + onboarded 6 SWE interns; shipped their PRs to prod within 2 weeks (100% retention).",
    ],
};

export default function RateMyCVPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState("upload"); // upload | analyzing | report
    const [mode, setMode] = useState("cv"); // cv | linkedin
    const [text, setText] = useState("");
    const [fileName, setFileName] = useState("");
    const [rating, setRating] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [rewriteBusy, setRewriteBusy] = useState(false);
    const [withHumanReview, setWithHumanReview] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState("");
    const [proStatus, setProStatus] = useState(null); // {active, pro_until, price_inr}
    const [proBusy, setProBusy] = useState(false);
    const [leadEmail, setLeadEmail] = useState("");
    const [leadSent, setLeadSent] = useState(false);
    const [liRewrite, setLiRewrite] = useState(null); // {headline, about, bullets}
    const [liRewriteBusy, setLiRewriteBusy] = useState(false);
    // V1.6.6 — In-place InlineSignupModal (progressive access). Replaces
    // the old page-nav to `/candidate/signup` so the user's CV upload
    // context isn't blown away when they need to authenticate.
    const [signupModal, setSignupModal] = useState({ open: false, context: "rating" });
    // V1.6.7 — Anon teaser preview. Set when an unauthed user hits Rate:
    // we call /public/cv/rate-preview → show a score band + top-3 gaps
    // with a locked-full-breakdown CTA (Grammarly pattern).
    const [preview, setPreview] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const fileInputRef = useRef(null);
    const persistedRef = useRef(false);
    const landingFiredRef = useRef(false);

    const isCandidate = user?.role === "candidate";

    // Fire landing_view once per session-mount (survives StrictMode double-invoke via ref).
    useEffect(() => {
        initPixels();
        // Persist ?ref and ?src into sessionStorage so they survive the signup
        // detour (`/candidate/login → register`) and get forwarded to the
        // /auth/candidate/register call as `payload.ref` / `payload.src`.
        const q = new URLSearchParams(window.location.search);
        const r = q.get("ref");
        const s = q.get("src");
        if (r) sessionStorage.setItem("cv_ref", r);
        if (s) sessionStorage.setItem("cv_src", s);
        if (!landingFiredRef.current) {
            landingFiredRef.current = true;
            trackFunnel("landing_view", { authed: !!user });
        }
    }, [user]);

    // Fetch pro status once the candidate is authed.
    useEffect(() => {
        if (!isCandidate) return;
        let alive = true;
        api.get("/cv/pro/status")
            .then((r) => { if (alive) setProStatus(r.data); })
            .catch(() => { /* silent */ });
        return () => { alive = false; };
    }, [isCandidate]);

    // If user comes back after signup, replay any persisted CV.
    useEffect(() => {
        const persisted = sessionStorage.getItem("cv_rate_pending");
        if (persisted && isCandidate && !persistedRef.current) {
            persistedRef.current = true;
            setText(persisted);
            sessionStorage.removeItem("cv_rate_pending");
            // Auto-submit
            setTimeout(() => submitRating(persisted), 400);
        }
    }, [isCandidate]);

    const onFile = async (f) => {
        if (!f) return;
        setFileName(f.name);
        if (f.size > 4 * 1024 * 1024) {
            toast.error("File too large (max 4 MB).");
            return;
        }
        if (f.name.toLowerCase().endsWith(".pdf")) {
            // Upload as multipart when signed in
            return f;
        }
        // .txt or .doc — read and stuff into text
        const t = await f.text();
        setText(t);
    };

    const submitRating = async (rawText) => {
        const bodyText = (rawText ?? text ?? "").trim();
        const file = fileInputRef.current?.files?.[0];
        const isPdf = mode === "cv" && file && file.name.toLowerCase().endsWith(".pdf");

        if (mode === "linkedin") {
            if (bodyText.length < 200) {
                toast.error("Paste your LinkedIn About + Experience — at least a few paragraphs.");
                return;
            }
        } else if (!bodyText && !isPdf) {
            toast.error("Please upload a PDF or paste your CV.");
            return;
        }

        trackFunnel("upload_started", { mode, hasFile: !!isPdf });

        if (!isCandidate) {
            // V1.6.7 — Grammarly-style teaser flow. Fire the public preview
            // endpoint (no auth) so the user sees their score + top-3 gaps
            // BEFORE any signup ask. The preview UI itself renders the
            // "Unlock full breakdown → Sign up" CTA which opens the
            // InlineSignupModal.
            sessionStorage.setItem("cv_rate_pending", bodyText || fileName);
            trackFunnel("signup_prompted", { mode });
            setPreviewBusy(true);
            try {
                let res;
                if (mode === "cv" && isPdf && file) {
                    const fd = new FormData();
                    fd.append("file", file);
                    res = await fetch(`${API}/public/cv/rate-preview`, {
                        method: "POST",
                        body: fd,
                    });
                } else {
                    res = await fetch(`${API}/public/cv/rate-preview`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ text: bodyText }),
                    });
                }
                const rawData = await res.json();
                if (!res.ok) {
                    // On rate-limit or error, fall through to the signup
                    // modal so the user still has a path forward.
                    if (res.status === 429) {
                        toast.info(rawData?.detail || "You've used your free previews for the hour.");
                    } else {
                        toast.error(rawData?.detail || "Couldn't generate preview.");
                    }
                    setSignupModal({ open: true, context: "rating" });
                    return;
                }
                // V1.8.9 — public preview now returns {job_id}; poll to
                // completion before showing the score.
                const data = await resolveAiResponse(rawData);
                setPreview(data);
                setStep("preview");
            } catch (err) {
                toast.error(err?.message || "Preview failed. Please try again.");
                setSignupModal({ open: true, context: "rating" });
            } finally {
                setPreviewBusy(false);
            }
            return;
        }

        setStep("analyzing");
        setSubmitting(true);
        try {
            let resp;
            if (mode === "linkedin") {
                resp = await api.post("/cv/linkedin/analyze", { text: bodyText });
            } else if (isPdf) {
                const fd = new FormData();
                fd.append("file", file);
                resp = await api.post("/cv/rate", fd);
            } else {
                resp = await api.post("/cv/rate", { text: bodyText });
            }
            // V1.8.9 — /cv/rate returns {job_id}; wait for completion.
            const finalData = await resolveAiResponse(resp.data);
            setRating(finalData);
            trackFunnel("rating_shown", { mode, score: finalData?.overall_score, band: finalData?.band });
            setStep("report");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI rating failed.");
            setStep("upload");
        } finally {
            setSubmitting(false);
        }
    };

    const doFreeRewrite = async () => {
        if (!rating) return;
        trackFunnel("rewrite_free_click", { rating_id: rating.id });
        setRewriteBusy(true);
        try {
            const { data: raw } = await api.post(`/cv/ratings/${rating.id}/rewrite`);
            const data = await resolveAiResponse(raw);
            setDownloadUrl(data.download_url);
            setRating((r) => ({ ...r, rewritten_pdf_id: data.rewritten_pdf_id, paid: false }));
            // Integration Priority 1: if a Resume Studio session was minted,
            // send the user to the new preview UI (better template gallery).
            if (data.resume_studio_url) {
                toast.success("Your improved CV is ready — opening Resume Studio…");
                setTimeout(() => {
                    window.location.href = data.resume_studio_url;
                }, 600);
                return;
            }
            toast.success("Your improved CV is ready.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rewrite failed.");
        } finally {
            setRewriteBusy(false);
        }
    };

    const doPaidCheckout = async () => {
        if (!rating) return;
        trackFunnel("rewrite_paid_click", { rating_id: rating.id, human_review: withHumanReview });
        setRewriteBusy(true);
        try {
            const rzOk = await loadRazorpay();
            if (!rzOk) throw new Error("Payment gateway didn't load. Please try again.");
            const { data: order } = await api.post(`/cv/ratings/${rating.id}/checkout`, {
                with_human_review: withHumanReview,
            });
            // Integration Priority 4: server may waive the fee if this user
            // already paid for Resume Studio Pro/Elite. In that case there's
            // no Razorpay dance — the rewrite is already done.
            if (order.already_covered) {
                setDownloadUrl(`/api/cv/ratings/${rating.id}/download`);
                setRating((r) => ({ ...r, rewritten_pdf_id: order.rewritten_pdf_id, paid: true }));
                trackFunnel("checkout_completed", {
                    rating_id: rating.id, human_review: false, waived: true,
                });
                toast.success(order.message || "Rewrite unlocked — already covered by your Resume Studio purchase.");
                setRewriteBusy(false);
                return;
            }
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy",
                description: withHumanReview
                    ? "AI CV rewrite + human review"
                    : "AI CV rewrite",
                theme: { color: "#2563EB" },
                handler: async (rp) => {
                    try {
                        const { data: verify } = await api.post(
                            `/cv/ratings/${rating.id}/verify`,
                            {
                                razorpay_order_id: rp.razorpay_order_id,
                                razorpay_payment_id: rp.razorpay_payment_id,
                                razorpay_signature: rp.razorpay_signature,
                            },
                        );
                        setDownloadUrl(verify.download_url);
                        setRating((r) => ({
                            ...r, rewritten_pdf_id: verify.rewritten_pdf_id, paid: true,
                            human_review: verify.human_review,
                        }));
                        trackFunnel("checkout_completed", { rating_id: rating.id, human_review: withHumanReview });
                        toast.success("Payment confirmed — your rewritten CV is ready.");
                    } catch (e) {
                        toast.error(e?.response?.data?.detail || "Payment verification failed.");
                    } finally {
                        setRewriteBusy(false);
                    }
                },
                modal: { ondismiss: () => setRewriteBusy(false) },
            });
            rz.open();
        } catch (e) {
            toast.error(e?.message || "Couldn't start payment.");
            setRewriteBusy(false);
        }
    };

    const downloadPdf = () => {
        if (!rating) return;
        trackFunnel("download_click", { rating_id: rating.id });
        const url = `${API}/cv/ratings/${rating.id}/download`;
        // See CandidateCVCard for iframe-sandbox rationale. Popup only inside
        // preview iframes; direct save everywhere else.
        const inIframe = window.self !== window.top;
        if (inIframe) {
            window.open(url, "_blank", "noopener");
        } else {
            const a = document.createElement("a");
            a.href = url;
            a.download = `NaukriDaddy_CV_${rating.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => document.body.removeChild(a), 200);
        }
        toast.success("Download started.");
    };

    const doProCheckout = async () => {
        if (!isCandidate) {
            // V1.6.6 — In-place signup instead of page-nav (see submitRating).
            setSignupModal({ open: true, context: "pro" });
            return;
        }
        trackFunnel("pro_subscribe_click");        setProBusy(true);
        try {
            const rzOk = await loadRazorpay();
            if (!rzOk) throw new Error("Payment gateway didn't load.");
            const { data: order } = await api.post("/cv/pro/checkout");
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy Pro",
                description: "30-day unlimited CV + LinkedIn analyzer + weekly gap tracker",
                theme: { color: "#2563EB" },
                handler: async (rp) => {
                    try {
                        const { data: verify } = await api.post("/cv/pro/verify", {
                            razorpay_order_id: rp.razorpay_order_id,
                            razorpay_payment_id: rp.razorpay_payment_id,
                            razorpay_signature: rp.razorpay_signature,
                        });
                        setProStatus({ active: true, pro_until: verify.pro_until, price_inr: order.amount_inr });
                        trackFunnel("pro_subscribed", { pro_until: verify.pro_until });
                        toast.success("Welcome to Pro — unlimited for the next 30 days.");
                    } catch (e) {
                        toast.error(e?.response?.data?.detail || "Pro activation failed.");
                    } finally { setProBusy(false); }
                },
                modal: { ondismiss: () => setProBusy(false) },
            });
            rz.open();
        } catch (e) {
            toast.error(e?.message || "Couldn't start Pro checkout.");
            setProBusy(false);
        }
    };

    const submitLead = async (e) => {
        e?.preventDefault?.();
        const email = leadEmail.trim().toLowerCase();
        if (!email || !email.includes("@")) {
            toast.error("Enter a valid email.");
            return;
        }
        try {
            await api.post("/cv/lead", {
                email,
                session_id: _funnelSessionId(),
                ref: new URLSearchParams(window.location.search).get("ref") || undefined,
                meta: { mode },
            });
            setLeadSent(true);
            trackFunnel("signup_prompted", { via: "email_capture" });
            toast.success("Got it. We'll email your score when it's ready.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save your email.");
        }
    };

    const doLinkedInRewrite = async () => {
        if (!rating) return;
        if (!proStatus?.active) {
            trackFunnel("pro_subscribe_click", { via: "linkedin_rewrite" });
            toast.info("LinkedIn rewrite is a Pro feature — unlock unlimited for ₹499/30 days.");
            return doProCheckout();
        }
        setLiRewriteBusy(true);
        try {
            const { data } = await api.post(`/cv/linkedin/${rating.id}/rewrite`);
            setLiRewrite({ headline: data.headline, about: data.about, bullets: data.bullets });
            toast.success("Your LinkedIn rewrite is ready — copy each block into your profile.");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) {
                toast.info("LinkedIn rewrite is Pro-only. Upgrade below.");
            } else {
                toast.error(err?.response?.data?.detail || "Rewrite failed.");
            }
        } finally {
            setLiRewriteBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink">
            <SeoMeta endpoint="/seo/page/rate-my-cv/meta" />
            {/* V1.6.6 — In-place signup modal (progressive access). Mounted
                at the top of the render so it stays visible regardless of
                `step` state (upload / analyzing / report). */}
            <InlineSignupModal
                open={signupModal.open}
                onClose={() => setSignupModal({ ...signupModal, open: false })}
                contextLine={
                    signupModal.context === "pro"
                        ? "Pro Monthly unlocks unlimited AI rewrites + weekly gap tracking."
                        : "So we can save your rating, track your progress, and share it with recruiters."
                }
                preservedDraft={
                    signupModal.context === "rating"
                        ? (text ? (text.length > 120 ? text.slice(0, 120) + "…" : text) : fileName)
                        : ""
                }
                onAuthed={async () => {
                    setSignupModal({ ...signupModal, open: false });
                    // AuthContext refresh handled inside the modal.
                    // When `isCandidate` flips true, our persist-and-replay
                    // effect will auto-submit the rating.
                }}
            />
            {/* Header */}
            <header className="bg-ink text-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Brand variant="light" size="h-7 sm:h-8" to={null} />
                    </Link>
                </div>
            </header>

            {step === "upload" && (
                <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
                    <div className="text-center mb-6 sm:mb-8">
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-3 inline-flex items-center gap-1.5">
                            <Sparkle size={11} weight="fill" /> Free AI-powered
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-ink mb-4">
                            {mode === "linkedin" ? "Rate my LinkedIn." : "Rate my CV."}
                        </h1>
                        <p className="text-base sm:text-lg text-[#525252] max-w-2xl mx-auto">
                            {mode === "linkedin"
                                ? "Get an honest LinkedIn profile audit — headline, About hook, and Experience — from NaukriDaddy AI."
                                : "Get an instant, honest score from NaukriDaddy AI — plus a prioritized gap list of what's costing you interviews."}
                        </p>
                    </div>

                    {/* Trust bar */}
                    <div
                        data-testid="trust-bar"
                        className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 border-y border-[#E5E5E5] py-4"
                    >
                        {TRUST_STATS.map((s) => (
                            <div key={s.label} className="text-center">
                                <p className="font-heading text-xl sm:text-3xl font-black tracking-tighter text-ink tabular-nums leading-none">
                                    {s.value}
                                    {s.suffix && <span className="text-[#A3A3A3] text-base">{s.suffix}</span>}
                                </p>
                                <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-[#525252] mt-1">
                                    {s.label}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Mode switch */}
                    <div
                        data-testid="mode-toggle"
                        className="flex bg-white border border-[#E5E5E5] p-1 mb-4 max-w-sm mx-auto"
                    >
                        <button
                            onClick={() => setMode("cv")}
                            data-testid="mode-cv"
                            className={`flex-1 py-2 text-[11px] font-black uppercase tracking-wider transition-colors ${mode === "cv" ? "bg-ink text-white" : "text-[#525252] hover:text-ink"}`}
                        >
                            My CV
                        </button>
                        <button
                            onClick={() => setMode("linkedin")}
                            data-testid="mode-linkedin"
                            className={`flex-1 py-2 text-[11px] font-black uppercase tracking-wider transition-colors ${mode === "linkedin" ? "bg-ink text-white" : "text-[#525252] hover:text-ink"}`}
                        >
                            LinkedIn <span className="text-[8px] align-top text-cobalt">NEW</span>
                        </button>
                    </div>

                    <div
                        data-testid="cv-upload-card"
                        className="bg-white border-2 border-dashed border-[#E5E5E5] hover:border-cobalt transition-colors p-6 sm:p-10"
                    >
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-14 h-14 bg-cobalt/10 text-cobalt mb-3">
                                <UploadSimple size={26} weight="bold" />
                            </div>
                            <p className="font-heading text-xl font-bold tracking-tight text-ink mb-1">
                                {mode === "linkedin" ? "Paste your LinkedIn text" : "Upload your CV"}
                            </p>
                            <p className="text-xs text-[#525252]">
                                {mode === "linkedin"
                                    ? "About + Experience sections · analyzed privately"
                                    : "PDF or text · up to 4 MB · stored securely"}
                            </p>
                        </div>

                        <div className="grid gap-3">
                            {mode === "cv" && (
                                <>
                                    <label
                                        htmlFor="cv-file"
                                        className="border border-[#E5E5E5] hover:border-ink transition-colors p-4 flex items-center gap-3 cursor-pointer bg-white"
                                        data-testid="cv-file-label"
                                    >
                                        <FileText size={18} weight="bold" className="text-[#525252] shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-ink truncate">
                                                {fileName || "Choose PDF or text file"}
                                            </p>
                                            <p className="text-[11px] text-[#A3A3A3]">
                                                {fileName ? "Ready to analyze" : "Click to browse or drag here"}
                                            </p>
                                        </div>
                                        <input
                                            id="cv-file"
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pdf,.txt,.doc,.docx"
                                            data-testid="cv-file-input"
                                            onChange={(e) => onFile(e.target.files?.[0])}
                                            className="hidden"
                                        />
                                    </label>

                                    <div className="text-center text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                        OR
                                    </div>
                                </>
                            )}

                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={
                                    mode === "linkedin"
                                        ? "Paste your LinkedIn About + Experience here… (open your profile → copy the About section and each role's description)"
                                        : "Paste your CV text here…"
                                }
                                data-testid="cv-paste-input"
                                rows={mode === "linkedin" ? 10 : 7}
                                className="w-full border border-[#E5E5E5] p-3 text-sm focus:outline-none focus:border-cobalt resize-y font-mono"
                            />
                        </div>

                        <button
                            onClick={() => submitRating()}
                            disabled={submitting || previewBusy || (mode === "cv" ? (!text && !fileName) : !text)}
                            data-testid="cv-rate-submit"
                            className="btn-gradient w-full mt-5 px-6 py-3.5 text-sm font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <Sparkle size={13} weight="fill" />
                            {previewBusy
                                ? "Scoring your CV…"
                                : mode === "linkedin" ? "Analyze my LinkedIn" : "Rate my CV free"}
                            <ArrowRight size={13} weight="bold" />
                        </button>
                        {!isCandidate && (
                            <p className="text-[11px] text-[#A3A3A3] text-center mt-3">
                                Free forever · Quick signup on your first upload · No credit card
                            </p>
                        )}
                    </div>

                    {/* Testimonials */}
                    <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="testimonials">
                        {TESTIMONIALS.map((t, i) => (
                            <blockquote
                                key={i}
                                className="border border-[#E5E5E5] bg-white p-4 relative"
                            >
                                <p className="text-xs text-ink leading-relaxed mb-2">&ldquo;{t.quote}&rdquo;</p>
                                <footer className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                    {t.who}
                                </footer>
                                <div className="absolute top-3 right-3 flex text-[#F59E0B]">
                                    {[0, 1, 2, 3, 4].map((k) => (
                                        <Star key={k} size={8} weight="fill" />
                                    ))}
                                </div>
                            </blockquote>
                        ))}
                    </div>

                    {/* Before/After preview strip — proves the value prop without needing a real rating */}
                    <div className="mt-8" data-testid="before-after-strip">
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-center text-cobalt mb-3">
                            The rewrite, in one glance
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div className="border border-[#DC2626]/30 bg-[#DC2626]/5 p-4">
                                <p className="text-[10px] font-black uppercase tracking-wider text-[#DC2626] mb-2">Before</p>
                                <ul className="text-xs text-[#525252] space-y-1.5 leading-relaxed">
                                    {BEFORE_AFTER.before.map((b, i) => (<li key={i}>— {b}</li>))}
                                </ul>
                            </div>
                            <div className="border border-[#059669]/30 bg-[#059669]/5 p-4">
                                <p className="text-[10px] font-black uppercase tracking-wider text-[#059669] mb-2">After — AI rewrite</p>
                                <ul className="text-xs text-ink space-y-1.5 leading-relaxed">
                                    {BEFORE_AFTER.after.map((b, i) => (<li key={i}>— {b}</li>))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Email capture (only shown to unauthed visitors) */}
                    {!isCandidate && !leadSent && (
                        <form
                            onSubmit={submitLead}
                            data-testid="email-capture"
                            className="mt-8 border border-[#E5E5E5] bg-white p-4 sm:p-5"
                        >
                            <div className="flex items-start gap-3 mb-3">
                                <div className="inline-flex items-center justify-center w-9 h-9 bg-cobalt/10 text-cobalt shrink-0">
                                    <EnvelopeSimple size={16} weight="bold" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-black text-ink">Not ready to sign up? Send my score to my inbox.</p>
                                    <p className="text-[11px] text-[#A3A3A3]">No spam. One email. Unsubscribe anytime.</p>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    type="email"
                                    inputMode="email"
                                    placeholder="you@work.com"
                                    value={leadEmail}
                                    onChange={(e) => setLeadEmail(e.target.value)}
                                    data-testid="lead-email-input"
                                    className="flex-1 border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                                />
                                <button
                                    type="submit"
                                    data-testid="lead-email-submit"
                                    className="bg-ink text-white hover:bg-cobalt px-5 py-2.5 text-xs font-black uppercase tracking-wider transition-colors"
                                >
                                    Email my score
                                </button>
                            </div>
                        </form>
                    )}
                    {leadSent && (
                        <div className="mt-8 border border-[#059669]/30 bg-[#059669]/5 p-4 text-center" data-testid="lead-sent">
                            <CheckCircle size={20} weight="fill" className="inline-block text-[#059669] mb-1" />
                            <p className="text-sm text-ink font-bold">On its way. Check your inbox in ~30 seconds.</p>
                        </div>
                    )}

                    {/* Trust strip */}
                    <div className="grid grid-cols-3 gap-4 mt-8 text-center">
                        <div>
                            <div className="inline-flex items-center justify-center w-10 h-10 bg-white border border-[#E5E5E5] text-cobalt mb-2">
                                <Lightning size={16} weight="fill" />
                            </div>
                            <p className="text-xs font-bold text-ink">30-second scan</p>
                        </div>
                        <div>
                            <div className="inline-flex items-center justify-center w-10 h-10 bg-white border border-[#E5E5E5] text-cobalt mb-2">
                                <ShieldCheck size={16} weight="fill" />
                            </div>
                            <p className="text-xs font-bold text-ink">Data stays private</p>
                        </div>
                        <div>
                            <div className="inline-flex items-center justify-center w-10 h-10 bg-white border border-[#E5E5E5] text-cobalt mb-2">
                                <Star size={16} weight="fill" />
                            </div>
                            <p className="text-xs font-bold text-ink">4 axis scoring</p>
                        </div>
                    </div>
                </section>
            )}

            {step === "analyzing" && (
                <section
                    data-testid="cv-analyzing"
                    className="max-w-2xl mx-auto px-4 sm:px-6 py-24 text-center"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-cobalt/10 text-cobalt mb-5 animate-pulse">
                        <Sparkle size={26} weight="fill" />
                    </div>
                    <h2 className="font-heading text-3xl font-black tracking-tighter text-ink mb-3">
                        {mode === "linkedin" ? "Auditing your LinkedIn…" : "Analyzing your CV…"}
                    </h2>
                    <p className="text-sm text-[#525252]">
                        NaukriDaddy AI is scoring against {mode === "linkedin"
                            ? "headline SEO, About hook, experience depth, and completeness."
                            : "ATS compatibility, clarity, impact, and skills coverage."}
                    </p>
                    <div className="mt-8 h-1.5 bg-[#F5F5F5] max-w-xs mx-auto overflow-hidden">
                        <div className="h-full bg-cobalt animate-pulse w-1/2" />
                    </div>
                </section>
            )}

            {/* V1.6.7 — Anon score preview (teaser). Shows score band +
                top-3 gaps upfront; locked sections tease what signup
                unlocks. */}
            {step === "preview" && preview && (
                <section
                    data-testid="cv-preview-teaser"
                    className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12"
                >
                    <div className="text-center mb-8">
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2">
                            Your instant preview
                        </p>
                        <div
                            className={`relative inline-flex flex-col items-center justify-center w-40 h-40 rounded-full ${(BAND_COLOR[preview.band] || BAND_COLOR.amber).bg} ring-8 ${(BAND_COLOR[preview.band] || BAND_COLOR.amber).ring} ring-opacity-20 mb-4 overflow-hidden`}
                            data-testid="cv-preview-score"
                        >
                            <span className={`text-5xl font-black ${(BAND_COLOR[preview.band] || BAND_COLOR.amber).fg}`}>
                                {(preview.overall_score ?? 0).toFixed(1)}
                            </span>
                            <span className="text-xs uppercase tracking-wider text-ink/50 mt-1">
                                / 10
                            </span>
                            {/* V1.6.9 — SAMPLE watermark (subtle) — the
                                prominent "Preview only · Sample" badge
                                below the score is the primary signal;
                                this is just a screenshot deterrent. */}
                            <span
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 flex items-center justify-center"
                            >
                                <span
                                    className="font-black text-black/10 tracking-[0.25em] whitespace-nowrap select-none"
                                    style={{
                                        fontSize: "1.05rem",
                                        transform: "rotate(-28deg)",
                                        letterSpacing: "0.25em",
                                        marginTop: "3.2rem",
                                    }}
                                >
                                    SAMPLE
                                </span>
                            </span>
                        </div>
                        <p
                            data-testid="cv-preview-sample-tag"
                            className="inline-flex items-center gap-1 mb-3 text-[9px] font-black uppercase tracking-[0.3em] text-amber-700 bg-amber-100 px-2.5 py-1"
                        >
                            <Sparkle size={9} weight="fill" />
                            Preview only · Sample
                        </p>
                        <h2 className="font-heading text-3xl font-black tracking-tighter mb-2">
                            {preview.band === "green" && "Solid CV — a few tweaks and you're ready."}
                            {preview.band === "amber" && "You're 70% there. Fix these and interviews click."}
                            {preview.band === "red" && "You're leaking interviews. Let's fix that."}
                        </h2>
                        <p className="text-sm text-ink/60 max-w-lg mx-auto">
                            {preview.summary_teaser}
                            {preview.summary_teaser?.length >= 180 ? "…" : ""}
                        </p>
                    </div>

                    {/* Top-3 gaps */}
                    <div className="bg-white border-2 border-ink/10 p-6 mb-6" data-testid="cv-preview-top-gaps">
                        <p className="text-[10px] font-black uppercase tracking-wider text-ink/50 mb-3">
                            Top 3 things to fix
                        </p>
                        <ul className="space-y-3">
                            {(preview.top_gaps || []).map((g, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 text-sm"
                                    data-testid={`cv-preview-gap-${i}`}
                                >
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 text-[10px] font-black shrink-0">
                                        {i + 1}
                                    </span>
                                    <span className="text-ink">{g}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Locked-full-breakdown CTA */}
                    <div
                        data-testid="cv-preview-locked-cta"
                        className="bg-gradient-to-br from-cobalt to-[#1E40AF] text-white p-6 sm:p-8 relative overflow-hidden"
                    >
                        <div className="relative z-10">
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/70 mb-2">
                                Locked · Free to unlock
                            </p>
                            <h3 className="font-heading text-2xl font-black mb-4">
                                Unlock the full breakdown
                            </h3>
                            <ul className="space-y-2 mb-6 text-sm">
                                {(preview.signup_unlocks || []).map((u, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <CheckCircle size={16} weight="fill" className="text-white shrink-0 mt-0.5" />
                                        <span>{u}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                data-testid="cv-preview-signup-btn"
                                onClick={() => setSignupModal({ open: true, context: "rating" })}
                                className="w-full sm:w-auto bg-white text-cobalt px-6 py-3.5 text-sm font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 hover:bg-cream transition-colors"
                            >
                                <Sparkle size={13} weight="fill" />
                                Sign up free · unlock now
                                <ArrowRight size={13} weight="bold" />
                            </button>
                            <p className="text-[11px] text-white/60 mt-3">
                                Magic-link login · No password · Takes 10 seconds
                            </p>
                        </div>
                    </div>

                    {preview.previews_remaining > 0 && (
                        <p className="text-center text-[11px] text-ink/40 mt-4">
                            {preview.previews_remaining} more free preview{preview.previews_remaining === 1 ? "" : "s"} left this hour
                        </p>
                    )}
                </section>
            )}

            {step === "report" && rating && (
                <section className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                    <ReportView
                        rating={rating}
                        rewriteBusy={rewriteBusy}
                        withHumanReview={withHumanReview}
                        setWithHumanReview={setWithHumanReview}
                        onFree={doFreeRewrite}
                        onPaid={doPaidCheckout}
                        onDownload={downloadPdf}
                        downloadUrl={downloadUrl}
                        proStatus={proStatus}
                        proBusy={proBusy}
                        onProCheckout={doProCheckout}
                        liRewrite={liRewrite}
                        liRewriteBusy={liRewriteBusy}
                        onLinkedInRewrite={doLinkedInRewrite}
                        onReset={() => { setRating(null); setStep("upload"); setText(""); setFileName(""); setDownloadUrl(""); setLiRewrite(null); }}
                    />
                </section>
            )}
        </div>
    );
}

function ReportView({ rating, rewriteBusy, withHumanReview, setWithHumanReview, onFree, onPaid, onDownload, downloadUrl, proStatus, proBusy, onProCheckout, liRewrite, liRewriteBusy, onLinkedInRewrite, onReset }) {
    const band = BAND_COLOR[rating.band] || BAND_COLOR.amber;
    const hasRewrite = !!rating.rewritten_pdf_id || !!downloadUrl;
    const canFree = rating.can_free_rewrite && !hasRewrite;
    const price = rating.paid_rewrite_price_inr || 299;
    const addon = rating.human_review_addon_inr || 99;

    // Sub-score bars
    const subs = useMemo(() => {
        const s = rating.sub_scores || {};
        return [
            { key: "ats_compat", label: "ATS compat", val: s.ats_compat },
            { key: "clarity", label: "Clarity", val: s.clarity },
            { key: "impact", label: "Impact", val: s.impact },
            { key: "skills", label: "Skills", val: s.skills },
        ];
    }, [rating]);

    return (
        <div data-testid="cv-report">
            {/* Hero score */}
            <div className={`border ${band.bg} p-6 sm:p-8 mb-6`}>
                <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center">
                    <div className={`w-32 h-32 rounded-full border-8 ${band.ring} bg-white flex flex-col items-center justify-center shrink-0`} data-testid="cv-score-ring">
                        <span className={`font-heading text-5xl font-black tracking-tighter ${band.fg} tabular-nums`}>
                            {rating.overall_score}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-wider text-[#525252]">/ 10</span>
                    </div>
                    <div className="min-w-0">
                        <p className={`text-[10px] font-black uppercase tracking-[0.25em] mb-1 ${band.fg}`}>
                            {rating.band === "green" ? "Strong CV" : rating.band === "amber" ? "Room to improve" : "Needs a rewrite"}
                        </p>
                        <p className="text-base sm:text-lg text-ink leading-relaxed" data-testid="cv-summary">
                            {rating.summary}
                        </p>
                    </div>
                </div>
                {/* Sub scores */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6" data-testid="cv-sub-scores">
                    {subs.map((s) => (
                        <div key={s.key} className="bg-white border border-[#E5E5E5] p-3">
                            <p className="text-[9px] font-black uppercase tracking-wider text-[#525252] mb-1">
                                {s.label}
                            </p>
                            <div className="flex items-baseline gap-1">
                                <span className="font-heading text-2xl font-black tracking-tighter text-ink tabular-nums">
                                    {s.val ?? "—"}
                                </span>
                                <span className="text-[10px] text-[#A3A3A3]">/10</span>
                            </div>
                            <div className="mt-1.5 h-1 bg-[#F5F5F5] overflow-hidden">
                                <div className="h-full bg-cobalt" style={{ width: `${((s.val || 0) / 10) * 100}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Gaps */}
            <div className="mb-6">
                <div className="flex items-baseline justify-between mb-3">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt">
                        Gaps to fix ({rating.gaps?.length || 0})
                    </p>
                    <p className="text-[10px] font-mono text-[#A3A3A3]">Sorted by impact</p>
                </div>
                <div className="space-y-2">
                    {(rating.gaps || []).map((g, i) => (
                        <article
                            key={i}
                            data-testid={`cv-gap-${i}`}
                            className={`border p-4 ${SEVERITY_TONE[g.severity] || "border-[#E5E5E5] bg-white"}`}
                        >
                            <div className="flex items-start justify-between gap-3 mb-1">
                                <p className="text-sm font-bold text-ink flex-1">{g.title}</p>
                                <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 border border-current opacity-70">
                                    {g.severity}
                                </span>
                            </div>
                            <p className="text-xs text-[#525252] leading-relaxed">{g.suggestion}</p>
                            {(g.keyword_examples || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {g.keyword_examples.map((kw, j) => (
                                        <span key={j} className="text-[10px] font-mono bg-white border border-[#E5E5E5] px-1.5 py-0.5">
                                            + {kw}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            </div>

            {/* Strengths */}
            {(rating.strengths || []).length > 0 && (
                <div className="mb-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#059669] mb-2 inline-flex items-center gap-1">
                        <CheckCircle size={11} weight="bold" /> What&apos;s working
                    </p>
                    <ul className="space-y-1">
                        {rating.strengths.map((s, i) => (
                            <li key={i} className="text-xs text-[#525252] flex gap-2">
                                <CheckCircle size={12} weight="fill" className="text-[#059669] shrink-0 mt-0.5" /> {s}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Rewrite CTA (CV mode only; LinkedIn ratings can't yet be rewritten) */}
            {!hasRewrite && rating.kind !== "linkedin" && (                <div className="border border-cobalt bg-cobalt/5 p-5 sm:p-6" data-testid="cv-rewrite-cta">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2 inline-flex items-center gap-1">
                        <Sparkle size={11} weight="fill" /> Fix these gaps with AI
                    </p>
                    <h3 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-ink mb-2">
                        {canFree ? "Your first rewrite is free." : `Continue for ₹${price}`}
                    </h3>
                    <p className="text-sm text-[#525252] mb-4">
                        NaukriDaddy AI will rewrite your CV to fix every gap above — without inventing anything.
                        You&apos;ll get a print-ready PDF in ~30 seconds.
                    </p>

                    {/* Human review upsell (paid tier only) */}
                    {!canFree && (
                        <label className="flex items-start gap-2 text-xs text-[#525252] mb-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={withHumanReview}
                                onChange={(e) => setWithHumanReview(e.target.checked)}
                                data-testid="cv-human-review-toggle"
                                className="mt-0.5 accent-cobalt"
                            />
                            <span>
                                <span className="font-bold text-ink">+ Human review</span> by a senior recruiter within 24h · <span className="font-mono">₹{addon}</span>
                            </span>
                        </label>
                    )}

                    <button
                        onClick={canFree ? onFree : onPaid}
                        disabled={rewriteBusy}
                        data-testid="cv-rewrite-btn"
                        className="btn-gradient px-6 py-3.5 text-sm font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {rewriteBusy
                            ? "Working…"
                            : canFree
                                ? "Rewrite my CV free"
                                : `Pay ₹${price + (withHumanReview ? addon : 0)} & rewrite`}
                        <ArrowRight size={13} weight="bold" />
                    </button>
                    {canFree && (
                        <p className="text-[10px] text-[#A3A3A3] mt-3">
                            One free rewrite per account. Additional rewrites are ₹{price} each.
                        </p>
                    )}
                </div>
            )}

            {/* LinkedIn rewrite (v5) — only for LinkedIn ratings */}
            {rating.kind === "linkedin" && (
                <div className="border border-[#0A66C2] bg-[#0A66C2]/5 p-5 sm:p-6" data-testid="li-rewrite-cta">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0A66C2] mb-2 inline-flex items-center gap-1">
                        <LinkedinLogo size={12} weight="fill" /> LinkedIn AI rewrite
                    </p>
                    <h3 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-ink mb-2">
                        Rewrite my profile to fix these gaps.
                    </h3>
                    <p className="text-sm text-[#525252] mb-4">
                        NaukriDaddy AI rewrites your headline, About, and top-role bullets for LinkedIn recruiter search.
                        You paste them straight into your profile — no auth handoff, no risk.
                    </p>
                    <button
                        onClick={onLinkedInRewrite}
                        disabled={liRewriteBusy}
                        data-testid="li-rewrite-btn"
                        className="btn-gradient px-6 py-3 text-sm font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {liRewriteBusy ? "Rewriting…" : (proStatus?.active ? "Rewrite my LinkedIn" : "Unlock rewrite — ₹499")}
                        <ArrowRight size={13} weight="bold" />
                    </button>
                    {!proStatus?.active && (
                        <p className="text-[10px] text-[#A3A3A3] mt-3">
                            Included with NaukriDaddy Pro (₹499 for 30 days · unlimited rewrites + weekly gap tracker).
                        </p>
                    )}
                </div>
            )}
            {rating.kind === "linkedin" && liRewrite && (
                <div className="border border-[#059669] bg-white p-5 sm:p-6 space-y-4" data-testid="li-rewrite-result">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#059669] inline-flex items-center gap-1">
                        <CheckCircle size={11} weight="fill" /> Your rewrite is ready
                    </p>
                    {liRewrite.headline && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Headline</p>
                            <p className="text-sm text-ink bg-[#FAFAFA] border border-[#E5E5E5] p-3 font-mono">{liRewrite.headline}</p>
                        </div>
                    )}
                    {liRewrite.about && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">About</p>
                            <p className="text-sm text-ink bg-[#FAFAFA] border border-[#E5E5E5] p-3 whitespace-pre-wrap leading-relaxed">{liRewrite.about}</p>
                        </div>
                    )}
                    {liRewrite.bullets?.length > 0 && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Top-role bullets</p>
                            <ul className="text-sm text-ink bg-[#FAFAFA] border border-[#E5E5E5] p-3 space-y-2 leading-relaxed">
                                {liRewrite.bullets.map((b, i) => (<li key={i}>· {b}</li>))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Pro Monthly upsell (v4) — shown when not already Pro */}            {onProCheckout && !proStatus?.active && (
                <div
                    data-testid="cv-pro-upsell"
                    className="mt-4 border border-[#0A0A0A] bg-[#0A0A0A] text-white p-5 sm:p-6"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#F59E0B] mb-2 inline-flex items-center gap-1">
                                <Star size={11} weight="fill" /> NaukriDaddy Pro
                            </p>
                            <h3 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-2">
                                Unlimited rewrites for 30 days.
                            </h3>
                            <ul className="text-sm text-white/80 space-y-1 mb-4">
                                <li>· Unlimited AI CV rewrites</li>
                                <li>· LinkedIn profile analyzer (unlimited)</li>
                                <li>· Weekly gap-tracker email until every gap is fixed</li>
                                <li>· Priority queue when we&apos;re busy</li>
                            </ul>
                        </div>
                        <div className="text-right shrink-0">
                            <p className="text-[10px] font-black uppercase tracking-wider text-white/60">Just</p>
                            <p className="font-heading text-4xl font-black tabular-nums">₹{proStatus?.price_inr || 499}</p>
                            <p className="text-[10px] text-white/60">/ 30 days</p>
                        </div>
                    </div>
                    <button
                        onClick={onProCheckout}
                        disabled={proBusy}
                        data-testid="cv-pro-subscribe"
                        className="bg-[#F59E0B] text-[#0A0A0A] hover:bg-[#FBBF24] px-6 py-3 text-sm font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {proBusy ? "Working…" : "Go Pro — ₹499"}
                        <ArrowRight size={13} weight="bold" />
                    </button>
                </div>
            )}
            {proStatus?.active && (
                <div className="mt-4 border border-[#F59E0B] bg-[#F59E0B]/5 p-4" data-testid="cv-pro-active">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#F59E0B] mb-1 inline-flex items-center gap-1">
                        <Star size={11} weight="fill" /> Pro active
                    </p>
                    <p className="text-xs text-[#525252]">
                        Unlimited rewrites until {new Date(proStatus.pro_until).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}.
                    </p>
                </div>
            )}

            {/* Download */}
            {hasRewrite && (
                <div className="border border-[#059669] bg-[#059669]/5 p-5 sm:p-6" data-testid="cv-rewrite-ready">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#059669] mb-2 inline-flex items-center gap-1">
                        <CheckCircle size={11} weight="fill" /> Ready
                    </p>
                    <h3 className="font-heading text-2xl font-black tracking-tighter text-ink mb-1">
                        Your improved CV is ready.
                    </h3>
                    <p className="text-sm text-[#525252] mb-4">
                        {rating.human_review
                            ? "A human reviewer will double-check within 24h — you'll be notified when done."
                            : "Print-ready PDF · NaukriDaddy AI rewrote every gap while preserving your real experience."}
                    </p>
                    <button
                        onClick={onDownload}
                        data-testid="cv-download-btn"
                        className="btn-gradient px-6 py-3.5 text-sm font-black uppercase tracking-wider inline-flex items-center gap-2"
                    >
                        <Download size={13} weight="bold" /> Download PDF
                    </button>
                </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-4 flex-wrap">
                <button
                    onClick={onReset}
                    data-testid="cv-restart"
                    className="text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-ink"
                >
                    Rate another CV
                </button>
                <button
                    onClick={async () => {
                        trackFunnel("share_click", { rating_id: rating.id });
                        try {
                            const { data } = await api.post(`/cv/ratings/${rating.id}/share`);
                            const url = `${window.location.origin}/api/share/cv/${data.slug}`;
                            const scoreLabel = `${rating.overall_score}/10`;
                            const shareText = `I got ${scoreLabel} on the NaukriDaddy AI CV rating. Rate yours free 👉`;
                            // Native share sheet first (WhatsApp/LinkedIn/etc. on mobile & modern browsers)
                            if (navigator.share) {
                                try {
                                    await navigator.share({
                                        title: `My CV scored ${scoreLabel}`,
                                        text: shareText,
                                        url,
                                    });
                                    toast.success("Shared!");
                                    return;
                                } catch (err) {
                                    if (err?.name === "AbortError") return;
                                }
                            }
                            // Clipboard fallback
                            let copied = false;
                            try {
                                await navigator.clipboard.writeText(`${shareText} ${url}`);
                                copied = true;
                            } catch (_) {
                                try {
                                    const ta = document.createElement("textarea");
                                    ta.value = `${shareText} ${url}`;
                                    ta.style.position = "fixed";
                                    ta.style.opacity = "0";
                                    document.body.appendChild(ta);
                                    ta.select();
                                    copied = document.execCommand("copy");
                                    document.body.removeChild(ta);
                                } catch (_) { /* ignore */ }
                            }
                            if (copied) {
                                toast.success("Share link copied — paste on LinkedIn or WhatsApp!");
                            } else {
                                toast(`Copy this: ${url}`, { duration: 15000 });
                            }
                        } catch (_) { toast.error("Couldn't create share link."); }
                    }}
                    data-testid="cv-share-btn"
                    className="text-xs font-bold uppercase tracking-wider text-cobalt hover:underline inline-flex items-center gap-1"
                >
                    Share my score
                </button>
                <Link
                    to="/candidate"
                    className="text-xs font-bold uppercase tracking-wider text-cobalt hover:underline"
                >
                    View my applications →
                </Link>
            </div>
        </div>
    );
}
